import React, { useEffect, useMemo, useState } from 'react';
import { Box, Grid, Card, CardContent, Typography, TextField, Button, Stack, List, ListItem, ListItemText } from '@mui/material';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { apiService } from '../../services/apiService';

const CompareMembers: React.FC = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const idsParam = params.get('ids') || '';
  const [idsInput, setIdsInput] = useState(idsParam);
  const ids = useMemo(() => idsParam.split(',').map(s => s.trim()).filter(Boolean), [idsParam]);
  const [data, setData] = useState<any[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!ids.length) { setData(null); return; }
      setLoading(true);
      setError(null);
      try {
        const resp = await apiService.compareMembers(ids);
        if (cancelled) return;
        const items = Array.isArray(resp?.data?.items) ? resp.data.items : resp.data?.items || [];
        setData(items);
      } catch (e: any) {
        if (!cancelled) setError(e?.message || 'Failed to load comparison');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [ids]);

  const onApply = () => {
    const usp = new URLSearchParams();
    if (idsInput.trim()) usp.set('ids', idsInput.trim());
    navigate({ pathname: '/compare/members', search: `?${usp.toString()}` });
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>Compare Members</Typography>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }}>
        <TextField label="Member IDs (comma-separated bioguide IDs)" value={idsInput} onChange={e => setIdsInput(e.target.value)} size="small" fullWidth />
        <Button variant="contained" onClick={onApply}>Compare</Button>
      </Stack>
      {error && <Typography color="error" variant="body2">{error}</Typography>}
      <Grid container spacing={2}>
        {(data || []).map((m: any, idx: number) => (
          <Grid item xs={12} md={6} key={idx}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>{m.name || m.bioguideId || m.id}</Typography>
                {m.error && <Typography color="error">Member not found</Typography>}
                <Typography variant="body2" color="textSecondary">Party: {m.party || '—'} • State: {m.state || '—'}</Typography>
                <List>
                  <ListItem>
                    <ListItemText primary={`Sponsored bills: ${m.sponsorCount ?? 'Unavailable'}`} />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary={`Cosponsored bills: ${m.cosponsorCount ?? 'Unavailable'}`} />
                  </ListItem>
                  {typeof m.bipartisan_score === 'number' && (
                    <ListItem>
                      <ListItemText primary={`Bipartisan score: ${m.bipartisan_score.toFixed(2)}`} />
                    </ListItem>
                  )}
                </List>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
      {loading && <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>Loading…</Typography>}
    </Box>
  );
};

export default CompareMembers;
